// HUMINT voice samples (ADR-0062).
//
// A voice sample is a capture of kind `voice_sample`: about half a minute of the subject's free
// speech, recorded in the workspace. It goes up through the capture endpoint, keyed by a client
// UUID that every retry repeats, and the audio worker makes one voiceprint of it, kept at the
// subject. It is not a recording (`audio`): nothing of it is transcribed, it names no language
// and no participants, and it is not listed among *Výsluchy*. Where its job stands is read with
// the subject's other captures (`humintSubjectPageApi.captureStates`): `processing`, then `done`,
// `no_speech` (too little speech for a voiceprint) or `failed`.
//
// Reference changes and removal are audited; removal preserves the source document.
// Imported by path, not via the barrel.
import { apiClient } from './client'
import type { CaptureResponse, CaptureUploadOptions } from './humint'
import { humintPath } from './humint-path'
import type { RecordingExtension } from './humint-recordings'

/** The capture kind a voice sample goes up as. */
export const VOICE_SAMPLE_CAPTURE_KIND = 'voice_sample'

/** The largest voice sample the capture endpoint stores (`capture_too_large` above it). */
export const MAX_VOICE_SAMPLE_BYTES = 10 * 1024 * 1024

/** A sample is well under a megabyte, but a field link is slow: the client gives it this long. */
export const VOICE_SAMPLE_UPLOAD_TIMEOUT_MS = 3 * 60_000

/** One voice sample upload: the exact bytes and what the server needs to store them. */
export interface VoiceSampleUpload {
  /** Client UUID, the idempotency key: every retry sends the same one. */
  captureId: string
  blob: Blob
  /** Hex SHA-256 of `blob`; the server refuses the upload when its own hash differs. */
  sha256: string
  /** The container it was recorded in, as the upload's file extension (the server sniffs the bytes). */
  imported?: boolean
  extension: RecordingExtension
  /** When the recording started, ISO; left out, the server takes the time it arrived. */
  capturedAt?: string | null
  /** The encounter the sample joins (`enc-<uuid>`); none when left out. */
  sameEncounterRef?: string | null
}

function voiceSampleForm(upload: VoiceSampleUpload): FormData {
  const form = new FormData()
  form.append('file', upload.blob, `${upload.captureId}.${upload.extension}`)
  form.append('capture_id', upload.captureId)
  form.append('sha256', upload.sha256)
  form.append('kind', VOICE_SAMPLE_CAPTURE_KIND)
  if (upload.imported) form.append('imported', 'true')
  if (upload.capturedAt) form.append('captured_at', upload.capturedAt)
  if (upload.sameEncounterRef) form.append('same_encounter_ref', upload.sameEncounterRef)
  return form
}

export const humintVoiceApi = {
  setReference: (caseId: string, subjectRef: string, captureId: string, reference: boolean) =>
    apiClient.patch<void>(
      humintPath(caseId, 'subjects', subjectRef, 'voice-samples', captureId, 'reference'),
      { reference }
    ),
  remove: (caseId: string, subjectRef: string, captureId: string) =>
    apiClient.delete<void>(humintPath(caseId, 'subjects', subjectRef, 'voice-samples', captureId)),
  /**
   * Store a voice sample as the subject's `voice_sample` capture; 201 for a new one, 200 for a
   * replay of the same id — which also runs a failed job again. COLLABORATOR, audited as
   * `capture.create`.
   */
  upload: (
    caseId: string,
    subjectRef: string,
    upload: VoiceSampleUpload,
    options: CaptureUploadOptions = {}
  ) =>
    apiClient.upload<CaptureResponse>(
      humintPath(caseId, 'subjects', subjectRef, 'captures'),
      voiceSampleForm(upload),
      { ...options, timeoutMs: VOICE_SAMPLE_UPLOAD_TIMEOUT_MS }
    ),
}
